/**
 * Default Template Generators for Action Modals - Progressive World Contact Ladder
 *
 * Each civic level provides unique ways to engage with the world:
 * - Explorer: Private noticing, first external touch
 * - Sense-Maker: Comparison + evidence from outside app
 * - Advocate: Expression meets audience
 * - Catalyst: Coordination + institutions enter
 * - Change-Maker: Contribution + consequences, opt-in
 */

import type {
  ActionModalLevelContent,
  ActionModalsByLevel,
  ActionModalLevelKey,
} from './actionModalBlockTypes';

// Generate unique IDs for blocks
let blockIdCounter = 0;
function generateBlockId(): string {
  return `block_${Date.now()}_${blockIdCounter++}`;
}

// Explorer defaults: PRIVATE NOTICING + FIRST EXTERNAL TOUCH (no links)
export function getExplorerDefaults(): ActionModalLevelContent {
  return {
    lookDeeper: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'richText',
          title: 'Behind the Song',
          body: 'Add the story behind this song - the context, creation, and what the artist intended to convey.',
        },
        {
          id: generateBlockId(),
          type: 'worldNoticingPrompt',
          title: 'Notice in the world',
          prompt: 'Over the next week, where might you notice this theme or pattern? Just observe, no action required.',
          placeholder: 'I might notice this when...',
        },
        {
          id: generateBlockId(),
          type: 'timeline',
          title: 'Context in Time',
          events: [
            { year: 'YYYY', label: 'Key event or milestone', detail: 'Brief explanation' },
            { year: 'YYYY', label: 'Another important moment', detail: 'Context or impact' },
          ],
        },
      ],
    },
    oneKeyInsight: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'selectOne',
          title: 'Pick one key insight',
          prompt: 'Which resonates most with you?',
          options: [
            { id: 'opt1', text: 'First insight option - add meaningful statement' },
            { id: 'opt2', text: 'Second insight option - another angle' },
            { id: 'opt3', text: 'Third insight option - different perspective' },
          ],
        },
      ],
    },
    exploreConnections: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'richText',
          body: 'Explore Connections is auto-generated from song tags. Related songs appear based on shared themes, regions, and topics.',
        },
      ],
    },
    takeItFurther: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'pathwaySelector',
          title: 'Choose your pathway',
          description: 'Pick one pathway that interests you',
          pathways: [
            {
              id: 'personal-practice',
              label: 'Personal Practice',
              description: 'Save for later, journal, or reflect privately on this song',
              icon: 'user',
            },
            {
              id: 'learning-context',
              label: 'Learning & Context',
              description: 'Learn more about the background, history, or themes in this song',
              icon: 'book-open',
            },
          ],
        },
      ],
    },
  };
}

// Sense-Maker defaults: COMPARISON + EVIDENCE OUTSIDE APP
export function getSenseMakerDefaults(): ActionModalLevelContent {
  return {
    lookDeeper: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'comparePrompt',
          title: 'Compare contexts',
          prompt: 'How does this issue show up in different contexts? Compare two examples, locations, or time periods.',
          leftLabel: 'One context',
          rightLabel: 'Another context',
          placeholder: 'Context 1: [describe] | Context 2: [describe] | How they differ: [explain]',
        },
        {
          id: generateBlockId(),
          type: 'timeline',
          title: 'Pattern over time',
          events: [
            { year: 'YYYY', label: 'Key event showing this pattern', detail: 'What happened' },
            { year: 'YYYY', label: 'Pattern repeats or evolves', detail: 'How it changed' },
            { year: 'YYYY', label: 'Current manifestation', detail: 'Where we are now' },
          ],
        },
      ],
    },
    oneKeyInsight: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'shortWrite',
          title: 'Write your synthesis',
          prompt: 'In 2-3 sentences, synthesize the core pattern or tension at play',
          placeholder: 'The pattern I see is...',
          minChars: 50,
          maxChars: 300,
        },
      ],
    },
    exploreConnections: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'richText',
          body: 'Explore Connections is auto-generated from song tags. Look for patterns across these songs - how do themes show up in different contexts?',
        },
      ],
    },
    takeItFurther: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'pathwaySelector',
          title: 'Choose your pathway',
          description: 'Pick one pathway that interests you',
          pathways: [
            {
              id: 'personal-practice',
              label: 'Personal Practice',
              description: 'Continue tracking patterns, journaling, or reflecting privately',
              icon: 'user',
            },
            {
              id: 'learning-context',
              label: 'Learning & Context',
              description: 'Research deeper - read articles, explore data, understand context',
              icon: 'book-open',
            },
            {
              id: 'bring-example',
              label: 'Bring One Example',
              description: 'Find one example of this pattern from outside Civicbeat (no link required)',
              icon: 'search',
            },
          ],
        },
        {
          id: generateBlockId(),
          type: 'evidencePrompt',
          title: 'Bring one example (optional)',
          prompt: 'If you chose "Bring One Example", describe something you found in news, research, or your community',
          placeholder: 'I found this example: [describe what you found and where]',
          requiresUrl: false,
        },
      ],
    },
  };
}

// Advocate defaults: EXPRESSION MEETS AUDIENCE
export function getAdvocateDefaults(): ActionModalLevelContent {
  return {
    lookDeeper: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'richText',
          title: "What's at stake",
          body: 'Explain what is at stake in this issue. Who is affected? What are the real-world consequences? Why does this matter now?',
        },
        {
          id: generateBlockId(),
          type: 'timeline',
          title: 'Events and consequences',
          events: [
            { year: 'YYYY', label: 'Event with consequences', detail: 'Who was affected' },
            { year: 'YYYY', label: 'Response or policy', detail: 'What changed (or didn\'t)' },
            { year: 'YYYY', label: 'Current stakes', detail: 'Why it matters today' },
          ],
        },
      ],
    },
    oneKeyInsight: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'audienceExplainerPrompt',
          title: 'Explain it to someone',
          prompt: 'How would you explain this issue to a friend, family member, or colleague? Practice explaining why it matters.',
          audienceOptions: ['A friend', 'A family member', 'A colleague', 'Someone who disagrees'],
          placeholder: 'If I were explaining this to [audience], I would say...',
        },
      ],
    },
    exploreConnections: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'richText',
          body: 'Explore Connections is auto-generated from song tags. These songs amplify similar voices and perspectives.',
        },
      ],
    },
    takeItFurther: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'pathwaySelector',
          title: 'Choose your pathway',
          description: 'Pick one pathway that interests you',
          pathways: [
            {
              id: 'expression-voice',
              label: 'Expression & Voice',
              description: 'Share with context - write, speak, or create about this issue',
              icon: 'message-circle',
            },
            {
              id: 'learning-context',
              label: 'Learning & Context',
              description: 'Learn more to strengthen your understanding (optional informational links)',
              icon: 'book-open',
            },
          ],
        },
        {
          id: generateBlockId(),
          type: 'shareTemplate',
          title: 'Share templates (optional)',
          templates: [
            {
              label: 'Social media',
              templateText: 'Listening to [Song] made me think about [issue]. Here\'s why it matters: [your insight]',
            },
            {
              label: 'Conversation',
              templateText: 'Have you heard [Song] by [Artist]? It made me realize [insight]. What do you think about...',
            },
          ],
        },
      ],
    },
  };
}

// Catalyst defaults: COORDINATION + INSTITUTIONS ENTER
export function getCatalystDefaults(): ActionModalLevelContent {
  return {
    lookDeeper: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'stakeholderMapPrompt',
          title: 'Stakeholders & leverage',
          prompt: 'Map the stakeholders: Who holds power? Who is organizing for change? Where are the leverage points?',
          categories: ['Power holders', 'Community organizations', 'Affected communities', 'Potential allies'],
          placeholder: 'Power holders: [list] | Organizers: [list] | Leverage points: [list]',
        },
        {
          id: generateBlockId(),
          type: 'timeline',
          title: 'Strategic timeline',
          events: [
            { year: 'YYYY', label: 'Organizing milestone', detail: 'Community action or campaign' },
            { year: 'YYYY', label: 'Institutional response', detail: 'Policy change or pushback' },
            { year: 'YYYY', label: 'Current landscape', detail: 'Where leverage exists now' },
          ],
        },
      ],
    },
    oneKeyInsight: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'insightCard',
          title: 'Theory of change',
          statement: 'Problem → Root Cause → Leverage Point',
          supportingPoints: [
            'The problem: [describe the issue clearly]',
            'Root cause: [underlying systemic factor]',
            'Leverage point: [where intervention can create change]',
          ],
        },
      ],
    },
    exploreConnections: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'richText',
          body: 'Explore Connections is auto-generated from song tags. Look for songs used in organizing or movement work.',
        },
      ],
    },
    takeItFurther: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'pathwaySelector',
          title: 'Choose your pathway',
          description: 'Pick one pathway that interests you',
          pathways: [
            {
              id: 'connection-community',
              label: 'Connection & Community',
              description: 'Attend a talk, forum, or community event (placeholder for vetted events)',
              icon: 'users',
            },
            {
              id: 'contextual-actions',
              label: 'Contextual Action Cards',
              description: 'Learn how organizations work on this issue, or support if you choose',
              icon: 'target',
            },
          ],
        },
        {
          id: generateBlockId(),
          type: 'actionCards',
          title: 'Action cards (editable per song)',
          items: [
            {
              title: 'Learn how [Org Name] works on this',
              orgName: 'Organization Name',
              region: 'Your Region',
              actionType: 'Learn',
              url: 'https://example.org',
              whyThisOrg: 'Brief explanation of why this org is recommended for this issue',
              safetyNote: 'Optional safety or context note',
            },
            {
              title: 'Support [Org Name] if you choose',
              orgName: 'Another Organization',
              region: 'National',
              actionType: 'Support',
              url: 'https://example.org',
              whyThisOrg: 'Why this organization is trustworthy and relevant',
            },
          ],
        },
      ],
    },
  };
}

// Change-Maker defaults: CONTRIBUTION + CONSEQUENCES, OPT-IN
export function getChangeMakerDefaults(): ActionModalLevelContent {
  return {
    lookDeeper: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'richText',
          title: 'Strategy and constraints',
          body: 'What is the strategic landscape? What resources exist? What are the constraints (political, economic, cultural)? What alliances are needed for systemic change?',
        },
        {
          id: generateBlockId(),
          type: 'timeline',
          title: 'Strategic opportunities',
          events: [
            { year: 'YYYY', label: 'Past intervention or strategy', detail: 'What was tried, what worked' },
            { year: 'YYYY', label: 'Current landscape', detail: 'Resources, constraints, opportunities' },
            { year: 'YYYY', label: 'Future potential', detail: 'Where strategic action could lead' },
          ],
        },
      ],
    },
    oneKeyInsight: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'insightCard',
          title: 'Strategic framing',
          statement: 'If [intervention] → Then [outcome] → Leading to [impact]',
          supportingPoints: [
            'Intervention: [specific strategy or action]',
            'Intermediate outcome: [measurable change]',
            'Ultimate impact: [systemic transformation]',
          ],
        },
      ],
    },
    exploreConnections: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'richText',
          body: 'Explore Connections is auto-generated from song tags. Look for songs documenting successful campaigns, policy changes, or movement victories.',
        },
      ],
    },
    takeItFurther: {
      blocks: [
        {
          id: generateBlockId(),
          type: 'pathwaySelector',
          title: 'Choose your pathway',
          description: 'Pick one pathway that interests you',
          pathways: [
            {
              id: 'contribution-action',
              label: 'Contribution & Action',
              description: 'Contribute skills, time, or resources to ongoing efforts',
              icon: 'zap',
            },
            {
              id: 'co-create',
              label: 'Co-create',
              description: 'Join or form a small group, working group, or civic lab',
              icon: 'users',
            },
          ],
        },
        {
          id: generateBlockId(),
          type: 'contributionCards',
          title: 'Contribution opportunities (editable per song)',
          items: [
            {
              title: 'Contribute [Skill Type] to [Partner]',
              partnerName: 'Partner Organization',
              region: 'Regional',
              contributionType: 'Skills',
              url: 'https://example.org',
              expectations: 'What this contribution involves, time commitment, skills needed',
              safetyNote: 'Important context about this opportunity',
            },
            {
              title: 'Join [Partner] working group',
              partnerName: 'Another Partner',
              region: 'Local',
              contributionType: 'Time',
              url: 'https://example.org',
              expectations: 'What the working group does, meeting frequency, commitment level',
              safetyNote: 'Context about group norms or requirements',
            },
          ],
        },
        {
          id: generateBlockId(),
          type: 'reflectionPrompt',
          title: 'Build collective power',
          prompt: 'Who could you invite to co-explore or co-create around this issue? Think about your networks, skills, and resources.',
          placeholder: 'I could connect with...',
          minChars: 50,
          maxChars: 1000,
        },
      ],
    },
  };
}

// Get defaults for a specific level
export function getDefaultsForLevel(levelKey: ActionModalLevelKey): ActionModalLevelContent {
  switch (levelKey) {
    case 'explorer':
      return getExplorerDefaults();
    case 'senseMaker':
      return getSenseMakerDefaults();
    case 'advocate':
      return getAdvocateDefaults();
    case 'catalyst':
      return getCatalystDefaults();
    case 'changeMaker':
      return getChangeMakerDefaults();
    default:
      // Observer uses existing fields, not block system
      return {
        lookDeeper: { blocks: [] },
        oneKeyInsight: { blocks: [] },
        exploreConnections: { blocks: [] },
        takeItFurther: { blocks: [] },
      };
  }
}

// Generate placeholder blocks for missing content
export function getPlaceholderBlocks(): ActionModalLevelContent {
  const placeholderBlock = {
    id: generateBlockId(),
    type: 'placeholder' as const,
    title: 'Content coming soon',
    body: 'Level-specific content for this section is being developed. Check back soon for guided activities and resources tailored to your civic journey.',
  };

  return {
    lookDeeper: { blocks: [placeholderBlock] },
    oneKeyInsight: { blocks: [{ ...placeholderBlock, id: generateBlockId() }] },
    exploreConnections: { blocks: [{ ...placeholderBlock, id: generateBlockId() }] },
    takeItFurther: { blocks: [{ ...placeholderBlock, id: generateBlockId() }] },
  };
}

// Ensure action modals by level has defaults for all non-Observer levels
export function ensureDefaults(
  actionModalsByLevel: ActionModalsByLevel | undefined
): ActionModalsByLevel {
  const result: ActionModalsByLevel = actionModalsByLevel || {};

  // Don't auto-generate Observer content - it uses legacy fields
  // For other levels, use defaults if content is missing

  const levels: ActionModalLevelKey[] = ['explorer', 'senseMaker', 'advocate', 'catalyst', 'changeMaker'];

  levels.forEach(level => {
    if (!result[level]) {
      result[level] = getDefaultsForLevel(level);
    }
  });

  return result;
}
